import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import {
  Badge, Banner, Combobox, EmptyState, PageLoading, SearchInput, Select,
  TextArea, TextInput, Toggle,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import { GST_RATES, money } from './orderMeta';
import ProductImport from './ProductImport';

import type {
  Product, ProductAttribute, ProductAttributeKind, ProductCategory, ProductList,
} from '../../types';

/** The three master lists a product form picks from. */
type Masters = Record<ProductAttributeKind, string[]>;
const EMPTY_MASTERS: Masters = { category: [], brand: [], unit: [] };

/**
 * The product master.
 *
 * Two things this screen is built around:
 *
 *   1. **A product with no price cannot be ordered.** So "priced in N
 *      lists" is a column, not a detail — an admin who adds forty
 *      products and prices none of them has done no work at all, and
 *      finding that out from a rep who cannot book is the expensive way.
 *
 *   2. **Nobody types 800 SKUs.** Import is a primary action, not
 *      something in a menu.
 */
export default function ProductsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [list, setList] = useState<ProductList | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [masters, setMasters] = useState<Masters>(EMPTY_MASTERS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [products, cats] = await Promise.all([
        api.get<ProductList>('/orders/products', {
          search: search || undefined, category: category || undefined, limit: 200,
        }),
        api.get<ProductCategory[]>('/orders/products/categories'),
      ]);
      setList(products);
      setCategories(cats);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load products.');
    } finally {
      setLoading(false);
    }
  }, [search, category, toast]);

  const loadMasters = useCallback(async () => {
    try {
      const [cat, brand, unit] = await Promise.all([
        api.get<ProductAttribute[]>('/orders/product-attributes', { kind: 'category', activeOnly: true }),
        api.get<ProductAttribute[]>('/orders/product-attributes', { kind: 'brand', activeOnly: true }),
        api.get<ProductAttribute[]>('/orders/product-attributes', { kind: 'unit', activeOnly: true }),
      ]);
      setMasters({
        category: cat.map((a) => a.name),
        brand: brand.map((a) => a.name),
        unit: unit.map((a) => a.name),
      });
    } catch {
      // A form that cannot reach the masters still works as free text.
    }
  }, []);

  // Inline "+ Add new": persist to the master, then reflect it locally so
  // the just-added value is immediately selectable without a round trip.
  const createAttr = useCallback(async (kind: ProductAttributeKind, name: string) => {
    try {
      await api.post('/orders/product-attributes', { kind, name });
    } catch (err) {
      if (err instanceof ApiClientError && err.status !== 409) throw err;
    }
    setMasters((m) => (m[kind].some((v) => v.toLowerCase() === name.toLowerCase())
      ? m : { ...m, [kind]: [...m[kind], name].sort((a, b) => a.localeCompare(b)) }));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => { void load(); }, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  useEffect(() => { void loadMasters(); }, [loadMasters]);

  const unpriced = (list?.products ?? []).filter(
    (p) => p.isActive && (p.pricedInLists ?? 0) === 0,
  ).length;

  return (
    <>
      <PageHeader
        title="Products"
        subtitle="What your team can sell. A product needs a price on a list before a rep can order it."
        actions={isAdmin ? (
          <>
            <a className="btn btn-ghost" href="/orders/masters">
              <Icon name="sliders" size={16} /> Manage lists
            </a>
            <button type="button" className="btn btn-ghost" onClick={() => setImporting(true)}>
              <Icon name="plus" size={16} /> Import
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              <Icon name="plus" size={16} /> Add product
            </button>
          </>
        ) : undefined}
      />

      <div className="page col gap-16">
        <div className="toolbar between">
          <div className="row gap-8">
            <SearchInput value={search} onChange={setSearch} placeholder="Search name or code" />
            {categories.length > 0 ? (
              <Select value={category} onChange={(e) => setCategory(e.target.value)}
                aria-label="Category">
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.name} value={c.name}>{c.name} ({c.productCount})</option>
                ))}
              </Select>
            ) : null}
          </div>
          {list ? (
            <span className="small faint">{list.products.length} product
              {list.products.length === 1 ? '' : 's'}</span>
          ) : null}
        </div>

        {unpriced > 0 ? (
          <Banner kind="warning">
            <strong>
              {unpriced} product{unpriced === 1 ? ' has' : 's have'} no price on any list.
            </strong>
            <div style={{ marginTop: 4 }}>
              Reps cannot order {unpriced === 1 ? 'it' : 'them'} until {unpriced === 1 ? 'it is' : 'they are'} priced.
              Set prices on the <a href="/orders/price-lists">price lists</a> page.
            </div>
          </Banner>
        ) : null}

        {loading ? <PageLoading /> : !list || list.products.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title={search || category ? 'Nothing matches that' : 'No products yet'}
              body={search || category ? undefined
                : 'Add them one at a time, or bring in the whole catalogue from a spreadsheet.'}
              action={isAdmin && !search && !category ? (
                <div className="row gap-8" style={{ justifyContent: 'center' }}>
                  <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
                    Add a product
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setImporting(true)}>
                    Import a catalogue
                  </button>
                </div>
              ) : undefined}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Unit</th>
                    <th className="num">MRP</th>
                    <th className="num">GST</th>
                    <th>HSN</th>
                    <th className="num">Priced in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.products.map((product) => (
                    <tr
                      key={product.id}
                      className={isAdmin ? 'clickable' : undefined}
                      onClick={isAdmin ? () => setEditing(product) : undefined}
                    >
                      <td className="mono small nowrap">{product.code}</td>
                      <td className="primary">
                        {product.name}
                        {product.isFeatured ? (
                          <> <Badge tone="brand">Featured</Badge></>
                        ) : null}
                        {!product.isActive ? (
                          <> <Badge tone="neutral">Inactive</Badge></>
                        ) : null}
                      </td>
                      <td>{product.category ?? <span className="faint">—</span>}</td>
                      <td>{product.unit}</td>
                      <td className="num">{product.mrp == null ? '—' : money(product.mrp)}</td>
                      <td className="num">{product.gstRate}%</td>
                      <td className="mono small">{product.hsnCode ?? <span className="faint">—</span>}</td>
                      <td className="num">
                        {(product.pricedInLists ?? 0) === 0 ? (
                          <Badge tone="warning">Not priced</Badge>
                        ) : product.pricedInLists}
                      </td>
                      <td>{isAdmin ? <Icon name="edit" size={15} /> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing ? (
        <ProductDialog
          product={editing === 'new' ? null : editing}
          masters={masters}
          onCreateAttr={createAttr}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); void loadMasters(); }}
        />
      ) : null}

      {importing ? (
        <ProductImport
          onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); void load(); }}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function ProductDialog({
  product, masters, onCreateAttr, onClose, onSaved,
}: {
  product: Product | null;
  masters: Masters;
  onCreateAttr: (kind: ProductAttributeKind, name: string) => Promise<void>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    code: product?.code ?? '',
    name: product?.name ?? '',
    description: product?.description ?? '',
    category: product?.category ?? '',
    brand: product?.brand ?? '',
    unit: product?.unit ?? 'piece',
    hsnCode: product?.hsnCode ?? '',
    gstRate: product?.gstRate ?? 0,
    mrp: product?.mrp == null ? '' : String(product.mrp),
    isActive: product?.isActive ?? true,
    isFeatured: product?.isFeatured ?? false,
  });

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    setBusy(true);
    setErrors({});
    try {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        category: form.category.trim() || undefined,
        brand: form.brand.trim() || undefined,
        unit: form.unit.trim() || 'piece',
        hsnCode: form.hsnCode.trim() || undefined,
        gstRate: Number(form.gstRate),
        mrp: form.mrp === '' ? undefined : Number(form.mrp),
        isActive: form.isActive,
        isFeatured: form.isFeatured,
      };

      if (product) await api.patch(`/orders/products/${product.id}`, body);
      else await api.post('/orders/products', body);

      toast.success(product ? 'Saved.' : `${body.name} added.`);
      onSaved();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setErrors(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error('Could not save that.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!product) return;
    setBusy(true);
    try {
      const result = await api.del<{ orderedTimes: number; note?: string }>(
        `/orders/products/${product.id}`,
      );
      toast.success(result.note ?? `${product.name} retired.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not retire that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={product ? product.name : 'Add a product'}
      subtitle={product ? product.code : 'It becomes orderable once it has a price on a list.'}
      onClose={onClose}
      footer={(
        <>
          {product ? (
            <button type="button" className="btn btn-danger-ghost" disabled={busy}
              onClick={() => void remove()}>
              Retire
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy}
            onClick={() => void save()}>
            {product ? 'Save' : 'Add product'}
          </button>
        </>
      )}
    >
      <div className="col gap-4">
        <div className="row gap-12">
          <div style={{ flex: 1 }}>
            <TextInput
              label="Code"
              value={form.code}
              error={errors.code}
              placeholder="SOAP-100"
              hint="Your own SKU. It is what an import matches on."
              onChange={(e) => set({ code: e.target.value })}
            />
          </div>
          <div style={{ flex: 2 }}>
            <TextInput
              label="Name"
              value={form.name}
              error={errors.name}
              placeholder="Lifeline Soap 100g"
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
        </div>

        <div className="row gap-12">
          <div style={{ flex: 1 }}>
            <Combobox
              label="Category"
              value={form.category}
              options={masters.category}
              placeholder="Choose or add"
              onChange={(category) => set({ category })}
              onCreate={(name) => onCreateAttr('category', name)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Combobox
              label="Brand"
              value={form.brand}
              options={masters.brand}
              placeholder="Choose or add"
              onChange={(brand) => set({ brand })}
              onCreate={(name) => onCreateAttr('brand', name)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Combobox
              label="Unit"
              value={form.unit}
              options={masters.unit}
              placeholder="Choose or add"
              onChange={(unit) => set({ unit })}
              onCreate={(name) => onCreateAttr('unit', name)}
            />
          </div>
        </div>

        <div className="row gap-12">
          <div style={{ flex: 1 }}>
            <TextInput
              label="HSN code"
              value={form.hsnCode}
              placeholder="3401"
              hint="What the invoice needs"
              onChange={(e) => set({ hsnCode: e.target.value })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Select
              label="GST rate"
              value={String(form.gstRate)}
              onChange={(e) => set({ gstRate: Number(e.target.value) })}
            >
              {GST_RATES.map((rate) => (
                <option key={rate} value={rate}>{rate}%</option>
              ))}
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              label="MRP"
              type="number"
              step="0.01"
              value={form.mrp}
              hint="Printed price, not what you charge"
              onChange={(e) => set({ mrp: e.target.value })}
            />
          </div>
        </div>

        <TextArea
          label="Description"
          rows={2}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />

        <Toggle
          checked={form.isActive}
          label="Active — reps can order this"
          onChange={(isActive) => set({ isActive })}
        />

        <Toggle
          checked={form.isFeatured}
          label="Featured — floats to the top of the rep's order screen"
          onChange={(isFeatured) => set({ isFeatured })}
        />

        {product?.prices?.length ? (
          <div className="card" style={{ marginTop: 10 }}>
            <div className="card-head"><h3>Prices</h3></div>
            <div className="table-wrap">
              <table className="data">
                <tbody>
                  {product.prices.map((p) => (
                    <tr key={p.priceListId}>
                      <td>
                        {p.priceListName}
                        {p.isDefault ? <> <Badge tone="brand">Default</Badge></> : null}
                      </td>
                      <td className="num">
                        {p.price == null
                          ? <Badge tone="warning">Not sold on this list</Badge>
                          : money(p.price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
